import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import type { StockImportJob } from '../../lib/api/modules/pharmacy';

/**
 * Background stock imports (medopd-docs 142): which jobs this browser is following. The ids live in localStorage, so
 * a reload or opening another page keeps following them; the tracker below polls them wherever the person is in the
 * app and tells them when each one ends.
 */
const STORAGE_KEY = 'clinvea.pharmacyImportJobs';
const LAST_KEY = 'clinvea.pharmacyImportLast';
const listeners = new Set<() => void>();

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function write(key: string, ids: string[]) {
  try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* private mode: in-memory only */ }
  memory[key] = ids;
  listeners.forEach((l) => l());
}

const memory: Record<string, string[]> = { [STORAGE_KEY]: read(STORAGE_KEY), [LAST_KEY]: read(LAST_KEY) };

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function trackImportJob(id: string) {
  const ids = memory[STORAGE_KEY];
  if (!ids.includes(id)) write(STORAGE_KEY, [...ids, id]);
}

function untrack(id: string) {
  write(STORAGE_KEY, memory[STORAGE_KEY].filter((x) => x !== id));
  write(LAST_KEY, [id]); // the stock screen shows the last finished import until dismissed
}

export function dismissLastImport() {
  write(LAST_KEY, []);
}

/** Jobs still running, as ids. */
export function useTrackedImportJobs(): string[] {
  return useSyncExternalStore(subscribe, () => memory[STORAGE_KEY], () => memory[STORAGE_KEY]);
}

/** The last import that finished in this browser (until dismissed). */
export function useLastImportJob(): string | null {
  const ids = useSyncExternalStore(subscribe, () => memory[LAST_KEY], () => memory[LAST_KEY]);
  return ids[0] ?? null;
}

export const importJobKey = (id: string) => ['pharmacy-import-job', id] as const;

export async function fetchImportJob(id: string): Promise<StockImportJob> {
  return api.pharmacyMedications.importJob(id) as Promise<StockImportJob>;
}

/**
 * Mounted once in the staff layout. Polls every followed job every 1.5 s; when one ends it shows a toast, refreshes
 * the stock list and stops following it. Renders nothing.
 */
export function PharmacyImportTracker() {
  const { t } = useTranslation('pharmacy');
  const queryClient = useQueryClient();
  const ids = useTrackedImportJobs();
  const announced = useRef(new Set<string>());

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: importJobKey(id),
      queryFn: () => fetchImportJob(id),
      refetchInterval: 1500,
      retry: 1,
    })),
  });

  useEffect(() => {
    results.forEach((r, i) => {
      const id = ids[i];
      if (!id) return;
      // A job this account cannot see (another login, deleted): stop following it quietly.
      if (r.isError) { untrack(id); return; }
      const job = r.data;
      if (!job || (job.status !== 'COMPLETED' && job.status !== 'FAILED') || announced.current.has(id)) return;
      announced.current.add(id);
      if (job.status === 'COMPLETED') {
        toast.success(t('importJobDone', { file: job.fileName ?? '', created: job.createdCount, added: job.addedCount }), { duration: 6000 });
        queryClient.invalidateQueries({ queryKey: ['pharmacyMedications'] });
      } else {
        toast.error(t('importJobFailed', { file: job.fileName ?? '' }), { duration: 8000 });
      }
      untrack(id);
    });
  }, [results, ids, queryClient, t]);

  return null;
}
